import React, { useState } from "react";
import {
  doc,
  setDoc,
  collection,
  addDoc,
  serverTimestamp,
  Timestamp
} from "firebase/firestore";
import { db, auth } from "./firebase";
import { useAdmin } from "./hooks/useAdmin";

function calculatePoints(place, participants) {
  const raw =
    321 * Math.exp(-0.077 * place) * (0.39 * Math.log(participants) + 0.13);
  return Math.round(raw);
}

function convertFlymarkToJSON(rawText) {
  const lines = rawText.split("\n");
  const dancersByRound = new Map();
  const starDancers = new Set();
  let starCount = 0;

  let competitionName = "";
  let discipline = "Latin";
  let dance = "";
  let category = "";
  let timestamp = "";

  function standardizeDance(text) {
    text = text.toLowerCase().trim();
    // Handle abbreviations
    if (text === 'sa') return 'Samba';
    if (text === 'ch') return 'Cha Cha Cha';
    if (text === 'ru') return 'Rumba';
    if (text === 'pd') return 'Paso Doble';
    if (text === 'j') return 'Jive';
    
    // Handle full names
    if (text.includes('samba')) return 'Samba';
    if (text.includes('cha')) return 'Cha Cha Cha';
    if (text.includes('rumba')) return 'Rumba';
    if (text.includes('paso') || text.includes('doble')) return 'Paso Doble';
    if (text.includes('jive')) return 'Jive';
    if (text === 'hip-hop') return 'Hip Hop';
    if (text === 'sychrono') return 'Contemporary';
    return text;
  }

  function standardizeCategory(text) {
    text = text.toLowerCase().trim();
    let ageGroup = '';
    
    // Handle both plural and singular forms
    if (text.includes('juvenile') || text.includes('juveniles')) {
      ageGroup = text.includes('2') || text.includes('ii') ? 'Juvenile II' : 'Juvenile I';
    } else if (text.includes('junior') || text.includes('juniors')) {
      ageGroup = text.includes('2') || text.includes('ii') ? 'Junior II' : 'Junior I';
    } else if (text.includes('youth')) {
      ageGroup = 'Youth';
    } else if (text.includes('adult') || text.includes('adults')) {
      ageGroup = 'Adult';
    }
    
    return 'Solo ' + ageGroup;
  }

  // Keep track of current state while parsing
  let currentGroup = null;
  let isReadingResults = false;

  for (let raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    // Get competition name from first line
    if (!competitionName && lines[0] && lines[0].trim()) {
      competitionName = lines[0].trim();
      continue;
    }

    // Match category/dance header (e.g., "Juniors 1 Solo - Sa")
    const eventMatch = line.match(/^([^-]+)\s*-\s*([^-\s]+)\s*$/i);
    if (eventMatch) {
      const categoryPart = eventMatch[1].trim();
      const dancePart = eventMatch[2].trim();
      
      category = standardizeCategory(categoryPart);
      dance = standardizeDance(dancePart);
      
      discipline = ['Samba', 'Cha Cha Cha', 'Rumba', 'Paso Doble', 'Jive'].includes(dance) ? 'Latin' 
                : ['Hip Hop'].includes(dance) ? 'Hip Hop'
                : ['Contemporary'].includes(dance) ? 'Contemporary'
                : 'Latin';
                
      isReadingResults = true;
      continue;
    }

    // Match result lines from the "Final results" section
    if (line.startsWith('№')) {
      isReadingResults = true;
      continue;
    }

    if (isReadingResults) {
      const parts = line.split(/\t/);
      if (parts.length >= 5) {
        const placeStr = parts[1].trim();
        const name = parts[2].trim();
        const city = parts[3].trim();
        const club = parts[4].trim();

        // Skip header row
        if (placeStr === 'Place') continue;
        
        // Handle ties (e.g., "10 - 12")
        let place = placeStr;
        if (place.includes('-')) {
          place = parseInt(place.split('-')[0]);
        }
        place = parseInt(place);
        if (isNaN(place)) continue;

        // Use club if available, otherwise use city
        const studio = club !== '-' ? club : city;
        
        // Skip invalid entries
        if (
          name.length < 3 ||
          /^[A-Za-z]$/.test(name) ||
          /^\d+$/.test(name)
        ) continue;

        // Store result
        dancersByRound.set(`${name}||${studio}`, {
          dancerName: name,
          studio: studio,
          place: place,
          priority: 0  // All results are from final
        });
      }
    }
  }

  // Process all results
  const allResults = Array.from(dancersByRound.values());
  
  // Sort by place
  allResults.sort((a, b) => a.place - b.place);
  
  // Handle ties and adjust places
  let nextPlace = 1;
  let i = 0;
  while (i < allResults.length) {
    const current = allResults[i];
    const tiedDancers = allResults.filter(d => d.place === current.place);
    
    // Assign the same place to all tied dancers
    for (const dancer of tiedDancers) {
      dancer.place = nextPlace;
    }
    
    // Next place should skip over all tied positions
    nextPlace += tiedDancers.length;
    i += tiedDancers.length;
  }

  const validResults = allResults;
  const participantKeys = new Set(validResults.map((r) => `${r.dancerName}||${r.studio}`));

  const catId = category
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const datePart = timestamp.slice(0, 10).replace(/-/g, "");
  // Generate unique ID to prevent overwrites - include random component like UploadCompetition.js
  const randomSuffix = Math.random().toString(36).substr(2, 6);
  const id = `flymark_${datePart}_${dance.toLowerCase()}_${catId}_${randomSuffix}`;

  return {
    id,
    name: competitionName,
    discipline,
    dance,
    category,
    timestamp,
    participants: participantKeys.size,
    results: validResults
  };
}

export default function UploadFlymark() {
  const [jsonInput, setJsonInput] = useState("");
  const [rawText, setRawText] = useState("");
  const [uploadStatus, setUploadStatus] = useState("");
  const [multiplier, setMultiplier] = useState("");
  const [compName, setCompName] = useState("");
  const [competitionDate, setCompetitionDate] = useState("");
  const { isAdmin, loading: adminLoading } = useAdmin();

  // Show loading state while checking admin status
  if (adminLoading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2>🩰 Upload Flymark Results</h2>
        <p>Loading...</p>
      </div>
    );
  }

  // Show access denied if not admin
  if (!isAdmin) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2>🩰 Upload Flymark Results</h2>
        <div style={{ 
          padding: "2rem", 
          border: "1px solid #f5c6cb", 
          borderRadius: "4px",
          backgroundColor: "#f8d7da",
          color: "#721c24",
          marginTop: "2rem"
        }}>
          <h3>🔒 Access Denied</h3>
          <p>You need administrator privileges to access this page.</p>
          <p>Please contact an administrator if you believe this is an error.</p>
        </div>
      </div>
    );
  }

  const handleConvert = () => {
    try {
      if (!competitionDate) {
        setUploadStatus("❌ Please enter the competition date");
        return;
      }

      const converted = convertFlymarkToJSON(rawText);
      converted.name = compName || converted.name;
      converted.timestamp = `${competitionDate}T00:00:00Z`;
      setJsonInput(JSON.stringify(converted, null, 2));
      setUploadStatus(`✅ Converted ${converted.participants} dancers`);
    } catch (err) {
      setUploadStatus("❌ Conversion failed: " + err.message);
    }
  };

  const handleUpload = async () => {
    try {
      // Check authentication first
      if (!auth.currentUser) {
        throw new Error("You must be signed in to upload competitions");
      }

      console.log("Current user:", auth.currentUser.email);
      console.log("Is admin:", isAdmin);

      // Validate JSON input
      if (!jsonInput.trim()) {
        throw new Error("Please enter competition data");
      }

      const data = JSON.parse(jsonInput);
      console.log("Parsed data:", data);

      // Validate required fields
      if (!data.id || !data.timestamp) {
        throw new Error("Missing ID or date");
      }

      if (!compName && !data.name) {
        throw new Error("Competition name is required");
      }

      if (!competitionDate) {
        throw new Error("Competition date is required");
      }

      // Clean and validate data
      data.discipline = (data.discipline || "Latin").trim();
      data.category = (data.category || "").trim();
      data.dance = (data.dance || "").trim();

      // Convert timestamp
      if (typeof data.timestamp === "string") {
        const d = new Date(data.timestamp);
        if (isNaN(d.getTime())) {
          throw new Error("Invalid date format");
        }
        data.timestamp = Timestamp.fromDate(new Date(Date.UTC(
          d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0
        )));
      }

      // Validate and process results
      data.results = Array.isArray(data.results)
        ? data.results.map((r) => ({
            ...r,
            points: calculatePoints(r.place, data.participants)
          }))
        : [];

      if (data.results.length === 0) {
        throw new Error("No valid results found");
      }

      console.log("Uploading to Firebase:", {
        id: data.id,
        name: compName || data.name,
        discipline: data.discipline,
        dance: data.dance,
        category: data.category,
        participants: data.participants,
        resultsCount: data.results.length
      });

      setUploadStatus("⏳ Uploading to Firebase...");

      // First, try to upload the competition document
      console.log("Writing competition document...");
      await setDoc(doc(db, "competitions", data.id), {
        id: data.id,
        name: compName || data.name,
        discipline: data.discipline,
        dance: data.dance,
        category: data.category,
        timestamp: data.timestamp,
        multiplier: parseFloat(multiplier) || 1,
        participants: data.participants,
        results: data.results
      });

      console.log("Competition document written successfully");

      // Then, add to the competition logs
      console.log("Writing to competition logs...");
      await addDoc(collection(db, "competitionLogs"), {
        uploadedBy: auth.currentUser?.email || "Unknown",
        timestamp: serverTimestamp(),
        summary: `${compName || data.name} (${data.participants} participants)`
      });

      console.log("Competition log written successfully");
      setUploadStatus("✅ Upload successful!");
    } catch (error) {
      console.error("Upload error:", error);
      if (error.code) {
        setUploadStatus(`❌ Firebase error (${error.code}): ${error.message}`);
      } else {
        setUploadStatus(`❌ Upload failed: ${error.message}`);
      }
    }
  };

  return (
    <div className="upload-container">
      <h2>🩰 Upload Flymark Results</h2>

      <div className="form-group">
        <label>🏆 Competition Name:</label>
        <input
          type="text"
          placeholder="Optional: override name parsed from raw text"
          value={compName}
          onChange={(e) => setCompName(e.target.value)}
        />
      </div>

  // ...existing code...

      <div className="form-group">
        <label>�📅 Competition Date:</label>
        <input
          type="date"
          required
          value={competitionDate}
          onChange={(e) => setCompetitionDate(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label>📄 Paste Raw Results from Flymark:</label>
        <textarea
          rows={12}
          className="code-textarea"
          value={rawText}
          onChange={(e) => setRawText(e.target.value)}
          placeholder="Paste Flymark results here..."
        />
      </div>
      
      <button className="btn btn-primary" onClick={handleConvert}>
        Convert to JSON
      </button>

      <div className="form-group">
        <label>📋 Converted JSON (Review and Edit if Needed):</label>
        <textarea
          placeholder="Converted competition JSON..."
          rows={16}
          className="code-textarea"
          value={jsonInput}
          onChange={(e) => setJsonInput(e.target.value)}
        />
      </div>

      <div className="form-group form-group-inline">
        <label>🧮 Multiplier (default 1):</label>
        <input
          type="number"
          step="0.1"
          min="0.1"
          className="number-input"
          value={multiplier}
          onChange={(e) => setMultiplier(e.target.value)}
        />
      </div>

      <button className="btn btn-success btn-upload" onClick={handleUpload}>
        Upload to Firebase
      </button>
      
      {uploadStatus && (
        <div className="upload-status">
          {uploadStatus}
        </div>
      )}
    </div>
  );
}
